import React, { useRef } from 'react'
import Button from 'react-bootstrap/Button'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { faBan, faEye } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAppSelector } from 'src/redux/storeHooks'
import { Link } from 'react-router-dom'
import { useCustomerAccess } from 'src/hooks/useCustomerAccess'
import { shouldShowAction, getActionButtonProps, getFormFieldDisabled } from 'src/utils/customerAccessUtils'
import { usePermission } from 'src/hooks/usePermission'
import { isProductLinkedForLinkActions } from './linkingStatusUtils'

const renderTooltip = (text) => (
  <Tooltip id={`tooltip-${text.replace(/\s+/g, '-')}`}>
    <div
      style={{
        color: 'white',
        borderRadius: '6px',
        fontSize: '12px',
        textAlign: 'center',
      }}
    >
      {text}
    </div>
  </Tooltip>
)

interface LinkButtonGroupProps {
  rowData: any
  rowIndex: number
  onClick: (rowIndex: number) => void
  onCloseTooltip: () => void
  platformId?: string | number
  mainStore?: boolean
  erpStore?: boolean
  channels?: any[]
}

const LinkButtonGroup = ({
  rowData,
  rowIndex,
  onClick,
  onCloseTooltip,
  platformId,
  mainStore,
  erpStore = false,
  channels = [],
}: LinkButtonGroupProps) => {
  const {isRead, isWrite, isDelete} = usePermission();
  const triggerRef = useRef<any>(null)
  const { fetchedUserAccountData } = useAppSelector(state=>state.userAccountData)
  const customerAccess = useCustomerAccess()
  const handleClick = () => {
    onCloseTooltip()
    onClick(rowIndex)

    // Manually hide tooltip by blurring the button
    if (triggerRef.current) {
      triggerRef.current.blur()
    }
  }

  const checkLinkedCondition = isProductLinkedForLinkActions(rowData, channels, {
    mainStore,
    erpStore,
  })

  return (
    <div>
      {['ERROR', 'ERROR_UNLINKED'].includes(rowData?.sync_status) ?  (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(`A Failed Product can't be linked to a Source/ERP Channel Product`)}
          trigger={['hover', 'focus']}
        >
            <FontAwesomeIcon icon={faBan} className='text-danger'/>
        </OverlayTrigger>
      ) : !(checkLinkedCondition) ? (
        shouldShowAction(customerAccess, 'edit') && isWrite && (() => {
          const linkButtonProps = getActionButtonProps(customerAccess, 'edit')
          return (
            <OverlayTrigger
              placement="bottom"
              overlay={renderTooltip('Link to the product')}
              trigger={['hover', 'focus']}
            >
              <Button
                variant="outline-primary"
                onClick={handleClick}
                {...linkButtonProps}
                style={{
                  fontSize: '11px',
                  padding: '2px 6px',
                  border: '1px solid #0d6efd',
                  color: '#0d6efd',
                  backgroundColor: 'transparent',
                  ...linkButtonProps.style,
                }}
                ref={triggerRef}
                disabled={linkButtonProps.disabled || getFormFieldDisabled(customerAccess) && isWrite}
                title={linkButtonProps.title}
              >
                LINK
              </Button>
            </OverlayTrigger>
          )
        })()
      ) : (
        <div className="d-flex gap-2 mt-1">
          
          <OverlayTrigger
            placement="bottom"
            overlay={renderTooltip('View the linked product')}
            trigger={['hover', 'focus']}
          > 
          <div className='pb-2'>
            <Link
               to={`/product-variant-detail/${rowData?.id}/platform/${rowData?.platform_product?.platform}/product/${rowData?.platform_product?.id}/${platformId}`}
            >
              <Button
                variant="outline-secondary"
                style={{
                  fontSize: '11px',
                  padding: '2px 6px',
                  border: '1px solid gray',
                  color: 'gray',
                  backgroundColor: 'transparent',
                }}
                ref={triggerRef}
              >
                VIEW
              </Button>
            </Link>
            </div>
          </OverlayTrigger>

            {isWrite && shouldShowAction(customerAccess, 'edit') && (() => {
              const linkNewButtonProps = getActionButtonProps(customerAccess, 'edit')
              return (
                <OverlayTrigger
                  placement="bottom"
                  overlay={renderTooltip('Link a different product')}
                  trigger={['hover', 'focus']}
                >
                <div className='pb-2'>
                  <Button
                    variant="outline-primary"
                    onClick={handleClick}
                    {...linkNewButtonProps}
                    style={{
                      fontSize: '11px',
                      padding: '2px 6px',
                      border: '1px solid #0d6efd',
                      color: '#0d6efd',
                      backgroundColor: 'transparent',
                      ...linkNewButtonProps.style,
                    }}
                    ref={triggerRef}
                    disabled={linkNewButtonProps.disabled || getFormFieldDisabled(customerAccess) && isWrite}
                    title={linkNewButtonProps.title}
                  >
                    LINK NEW
                  </Button>
                </div>
                </OverlayTrigger>
              )
            })()}
        </div>
      )}
    </div>
  )
}

export default LinkButtonGroup
